import { useMemo } from 'react';
import type { SiteTimeSeries, TimeSeriesPoint, ChartDataset } from '../types';
import { CHART_COLORS } from '../constants';

interface ChartDataResult {
    labels: string[];
    datasets: ChartDataset[];
    minMax: { min: number; max: number };
}

export const useChartData = (
    timeSeries: SiteTimeSeries[],
    metric: 'uptime' | 'responseTime' | 'requests' | 'errors'
): ChartDataResult => {
    return useMemo(() => {
        if (timeSeries.length === 0) {
            return { labels: [], datasets: [], minMax: { min: 0, max: 100 } };
        }

        const labels = timeSeries[0].data.map(p => {
            const date = new Date(p.timestamp);
            return date.toLocaleDateString('ru-RU', {
                month: 'short',
                day: 'numeric',
                hour: date.getHours() % 4 === 0 ? '2-digit' : undefined,
            }).replace(', ', '\n');
        });

        const colors = [
            CHART_COLORS.primary,
            CHART_COLORS.success,
            CHART_COLORS.warning,
            CHART_COLORS.purple,
            CHART_COLORS.cyan,
        ];

        const datasets = timeSeries.map((series, index) => ({
            label: series.siteName,
            data: series.data.map(p => {
                const val = p[metric];
                return metric === 'uptime' ? Math.max(0, Math.min(100, val)) : val;
            }),
            color: colors[index % colors.length],
            fill: metric === 'uptime',
            tension: 0.4,
        }));

        const allValues = datasets.flatMap(d => d.data);
        const min = Math.min(...allValues);
        const max = Math.max(...allValues);
        const padding = (max - min) * 0.1;

        return {
            labels,
            datasets,
            minMax: {
                min: Math.max(0, min - padding),
                max: max + padding,
            },
        };
    }, [timeSeries, metric]);
};

export const useSparklineData = (
    data: TimeSeriesPoint[],
    metric: 'uptime' | 'responseTime'
): { trend: 'up' | 'down' | 'neutral'; values: number[] } => {
    return useMemo(() => {
        const values = data.map(p => p[metric]);
        const first = values[0] ?? 0;
        const last = values[values.length - 1] ?? 0;
        
        const diff = last - first;
        const threshold = metric === 'uptime' ? 0.1 : 10;
        
        let trend: 'up' | 'down' | 'neutral' = 'neutral';
        if (Math.abs(diff) > threshold) {
            trend = diff > 0 ? 'up' : 'down';
            if (metric === 'responseTime') {
                trend = diff > 0 ? 'down' : 'up';
            }
        }

        return { trend, values };
    }, [data, metric]);
};